import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import {
  ArrowRight,
  Briefcase,
  Building2,
  Calendar,
  Check,
  Copy,
  Loader2,
  Search,
  Send,
  User,
  Users,
  X,
} from "lucide-react";

import { AppShell } from "@/components/AppShell";
import { LoginGate } from "@/components/LoginGate";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { StatusBanner } from "@/components/ui/status-banner";
import { sendInvitation } from "@/lib/invitations";
import { useActiveClientId } from "@/hooks/useActiveClient";
import { NoClientPicked } from "@/components/NoClientPicked";
import { supabase } from "@/lib/supabase";
import { cn } from "@/lib/cn";
import type { Company, Exercise, Language, Search as JobSearch } from "@/lib/types";

export const Route = createFileRoute("/generate")({
  validateSearch: (s: Record<string, unknown>) => ({
    company_id: typeof s.company_id === "string" ? s.company_id : undefined,
    search_id: typeof s.search_id === "string" ? s.search_id : undefined,
  }),
  component: GeneratePage,
});

function GeneratePage() {
  return (
    <LoginGate
      title="DeepDive"
      subtitle="Iniciá sesión para enviar invitaciones."
    >
      {({ user, signOut }) => (
        <AppShell user={user} onLogout={signOut}>
          <GenerateContent />
        </AppShell>
      )}
    </LoginGate>
  );
}

interface ResultState {
  assignmentId: string;
  testUrl: string;
  emailSent: boolean;
  emailError?: string;
}

function GenerateContent() {
  const search = Route.useSearch();
  const [activeClientId] = useActiveClientId();
  // Si vino por URL (ej. desde el botón "Enviar prueba" en la búsqueda),
  // ese gana. Si no, usamos el cliente activo global del header.
  const company_id = search.company_id ?? activeClientId ?? undefined;
  const search_id = search.search_id;
  if (!company_id) {
    return (
      <NoClientPicked
        title="Elegí un cliente para enviar invitaciones"
        description="Las invitaciones siempre se envían en nombre de un cliente. Elegí uno desde el dropdown del header."
      />
    );
  }
  return <GenerateForm companyId={company_id} searchId={search_id} />;
}

function GenerateForm({
  companyId,
  searchId,
}: {
  companyId: string;
  searchId: string | undefined;
}) {
  const company_id = companyId;
  const search_id = searchId;
  const [candidateName, setCandidateName] = useState("");
  const [candidateEmail, setCandidateEmail] = useState("");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [expiresAt, setExpiresAt] = useState<string>(defaultExpiry());
  const [searchTerm, setSearchTerm] = useState("");
  const [result, setResult] = useState<ResultState | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const { data: company } = useQuery({
    queryKey: ["company", company_id],
    queryFn: async (): Promise<Company | null> => {
      if (!company_id) return null;
      const { data, error } = await supabase
        .from("companies")
        .select("id, name, rubro")
        .eq("id", company_id)
        .single();
      if (error) return null;
      return data as Company;
    },
    enabled: !!company_id,
  });

  const { data: jobSearch } = useQuery({
    queryKey: ["search", search_id],
    queryFn: async (): Promise<JobSearch | null> => {
      if (!search_id) return null;
      const { data, error } = await supabase
        .from("searches")
        .select("id, puesto, experience, technologies")
        .eq("id", search_id)
        .single();
      if (error) return null;
      return data as JobSearch;
    },
    enabled: !!search_id,
  });

  const { data: exercises = [], isLoading } = useQuery({
    queryKey: ["exercises", "for-invitations", { company_id }],
    queryFn: async () => {
      let q = supabase
        .from("exercises")
        .select("id, title, language, duration_min")
        .eq("is_active", true)
        .order("created_at", { ascending: false });
      if (company_id) q = q.eq("company_id", company_id);
      const { data, error } = await q;
      if (error) throw error;
      return (data ?? []) as Pick<
        Exercise,
        "id" | "title" | "language" | "duration_min"
      >[];
    },
  });

  const filtered = useMemo(() => {
    const t = searchTerm.trim().toLowerCase();
    if (!t) return exercises;
    return exercises.filter(
      (e) =>
        e.title.toLowerCase().includes(t) ||
        e.id.toLowerCase().includes(t) ||
        e.language.toLowerCase().includes(t)
    );
  }, [exercises, searchTerm]);

  const selectedExercises = useMemo(
    () => exercises.filter((e) => selectedIds.includes(e.id)),
    [exercises, selectedIds]
  );

  const totalDurationMin = selectedExercises.reduce(
    (sum, e) => sum + (e.duration_min ?? 30),
    0
  );

  const mutation = useMutation({
    mutationFn: () =>
      sendInvitation({
        candidateName: candidateName.trim(),
        candidateEmail: candidateEmail.trim(),
        exerciseIds: selectedIds,
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
        companyId: company_id ?? null,
        searchId: search_id ?? null,
      }),
    onSuccess: (res) => {
      setResult(res);
      setErrorMsg(null);
      // Reset form para enviar otra
      setCandidateName("");
      setCandidateEmail("");
      setSelectedIds([]);
    },
    onError: (err: Error) => {
      setErrorMsg(err.message);
      setResult(null);
    },
  });

  function toggleExercise(id: string) {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }

  function canSubmit() {
    return (
      candidateName.trim().length > 0 &&
      candidateEmail.trim().length > 0 &&
      /.+@.+\..+/.test(candidateEmail.trim()) &&
      selectedIds.length > 0 &&
      !mutation.isPending
    );
  }

  return (
    <div className="px-6 py-5">
      <div className="mb-8">
        <h1 className="text-2xl font-semibold tracking-tight">
          Enviar invitación
        </h1>
        <p className="text-muted-foreground text-sm mt-1">
          Creá un test, elegí los ejercicios y enviá el link por email.
        </p>
      </div>

      {(company || jobSearch) && (
        <ContextBanner company={company} search={jobSearch} />
      )}

      {result && (
        <SuccessPanel
          result={result}
          onDismiss={() => setResult(null)}
        />
      )}

      {errorMsg && (
        <div className="mb-6">
          <StatusBanner
            variant="error"
            title="No se pudo enviar la invitación"
            onDismiss={() => setErrorMsg(null)}
          >
            {errorMsg}
          </StatusBanner>
        </div>
      )}

      <div className="space-y-6 rounded-[var(--radius)] border border-border bg-card p-6">
        {/* === Candidato === */}
        <Section
          icon={<User className="h-4 w-4" />}
          title="Candidato"
          description="A quién le mandamos el test."
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="name">Nombre completo</Label>
              <Input
                id="name"
                placeholder="Juan Pérez"
                value={candidateName}
                onChange={(e) => setCandidateName(e.target.value)}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="juan@empresa.com"
                value={candidateEmail}
                onChange={(e) => setCandidateEmail(e.target.value)}
                required
              />
            </div>
          </div>
        </Section>

        <Separator />

        {/* === Ejercicios === */}
        <Section
          icon={<Users className="h-4 w-4" />}
          title="Ejercicios"
          description={
            selectedIds.length === 0
              ? "Elegí uno o más."
              : `${selectedIds.length} seleccionado${selectedIds.length === 1 ? "" : "s"} · ${totalDurationMin} min en total`
          }
        >
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
            <Input
              placeholder="Filtrar por título, lenguaje o ID"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 h-9 text-sm"
            />
          </div>

          <div className="mt-3 rounded-md border border-border max-h-72 overflow-auto divide-y divide-border">
            {isLoading && (
              <div className="flex items-center justify-center py-10">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            )}
            {!isLoading && filtered.length === 0 && (
              <div className="px-3 py-8 text-center text-sm text-muted-foreground">
                {searchTerm
                  ? "Ningún ejercicio matchea."
                  : "Sin ejercicios. Creá uno en /admin."}
              </div>
            )}
            {filtered.map((e) => {
              const checked = selectedIds.includes(e.id);
              return (
                <button
                  key={e.id}
                  type="button"
                  onClick={() => toggleExercise(e.id)}
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-accent transition-colors",
                    checked && "bg-primary/5"
                  )}
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={() => toggleExercise(e.id)}
                    onClick={(ev) => ev.stopPropagation()}
                  />
                  <LangBadge language={e.language} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">
                      {e.title || "Sin título"}
                    </div>
                    <div className="text-[11px] text-muted-foreground font-mono truncate">
                      {e.id}
                    </div>
                  </div>
                  <span className="text-[11px] text-muted-foreground shrink-0">
                    {e.duration_min} min
                  </span>
                </button>
              );
            })}
          </div>
        </Section>

        <Separator />

        {/* === Expiración === */}
        <Section
          icon={<Calendar className="h-4 w-4" />}
          title="Vencimiento de la invitación"
          description="Si el candidato no entra antes de esta fecha, la invitación queda inválida."
        >
          <div className="flex items-center gap-3">
            <Input
              type="datetime-local"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              className="max-w-[260px]"
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setExpiresAt("")}
            >
              Sin vencimiento
            </Button>
          </div>
        </Section>
      </div>

      {/* === CTA === */}
      <div className="mt-6 flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          {selectedIds.length > 0 && candidateName.trim() && candidateEmail.trim()
            ? `Listo para enviar a ${candidateEmail.trim()}.`
            : "Completá todos los campos para enviar."}
        </p>
        <Button
          variant="primary"
          size="lg"
          onClick={() => mutation.mutate()}
          disabled={!canSubmit()}
          loading={mutation.isPending}
        >
          <Send className="h-4 w-4" />
          Enviar invitación
        </Button>
      </div>
    </div>
  );
}

// =================== SECTION ===================
function Section({
  icon,
  title,
  description,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <span className="text-primary">{icon}</span>
        <div>
          <h3 className="text-sm font-semibold tracking-tight">{title}</h3>
          {description && (
            <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
          )}
        </div>
      </div>
      {children}
    </div>
  );
}

// =================== SUCCESS PANEL ===================
function SuccessPanel({
  result,
  onDismiss,
}: {
  result: ResultState;
  onDismiss: () => void;
}) {
  const [copied, setCopied] = useState(false);

  function copy() {
    navigator.clipboard.writeText(result.testUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  if (!result.emailSent) {
    return (
      <div className="mb-6">
        <StatusBanner
          variant="warning"
          title="Invitación creada, pero el email falló"
          onDismiss={onDismiss}
        >
          <div className="space-y-2 mt-1">
            <p>{result.emailError ?? "Error desconocido"}.</p>
            <div className="flex items-center gap-2 pt-1">
              <code className="flex-1 text-[11px] bg-warning/10 border border-warning/20 px-2 py-1 rounded truncate">
                {result.testUrl}
              </code>
              <Button
                type="button"
                variant="default"
                size="sm"
                onClick={copy}
              >
                {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                {copied ? "Copiado" : "Copiar link"}
              </Button>
            </div>
          </div>
        </StatusBanner>
      </div>
    );
  }

  return (
    <div className="mb-6">
      <StatusBanner
        variant="success"
        title="Invitación enviada"
        onDismiss={onDismiss}
      >
        <div className="space-y-2 mt-1">
          <p>El candidato recibió el email. También podés compartir el link directamente:</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-[11px] bg-success/10 border border-success/20 px-2 py-1 rounded truncate text-foreground">
              {result.testUrl}
            </code>
            <Button
              type="button"
              variant="default"
              size="sm"
              onClick={copy}
            >
              {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
              {copied ? "Copiado" : "Copiar"}
            </Button>
            <Button type="button" variant="ghost" size="sm" asChild>
              <a href={result.testUrl} target="_blank" rel="noreferrer">
                Abrir <ArrowRight className="h-3 w-3" />
              </a>
            </Button>
          </div>
        </div>
      </StatusBanner>
    </div>
  );
}

// =================== LANG BADGE ===================
function LangBadge({ language }: { language: Language }) {
  const map = {
    typescript: { label: "TS", className: "bg-info/15 text-info border-info/30" },
    javascript: {
      label: "JS",
      className: "bg-warning/15 text-warning border-warning/30",
    },
    sql: { label: "SQL", className: "bg-success/15 text-success border-success/30" },
  } as const;
  const c = map[language];
  return (
    <Badge
      variant="outline"
      className={cn("normal-case text-[10px] tracking-normal", c.className)}
    >
      {c.label}
    </Badge>
  );
}

// =================== CONTEXT BANNER ===================
function ContextBanner({
  company,
  search,
}: {
  company: Company | null | undefined;
  search: JobSearch | null | undefined;
}) {
  return (
    <div className="mb-6 rounded-[var(--radius)] border border-primary/40 bg-primary/5 p-4 flex items-start gap-3">
      <div className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-primary/10 border border-primary/30 shrink-0">
        {search ? (
          <Briefcase className="h-4 w-4 text-primary" />
        ) : (
          <Building2 className="h-4 w-4 text-primary" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-primary mb-1">
          Invitación vinculada
        </div>
        {company && (
          <div className="text-sm">
            <Link
              to="/clients/$clientId"
              params={{ clientId: company.id }}
              className="font-medium hover:underline"
            >
              {company.name}
            </Link>
            {company.rubro && (
              <span className="text-muted-foreground ml-1">
                · {company.rubro}
              </span>
            )}
          </div>
        )}
        {search && (
          <div className="text-xs text-muted-foreground mt-0.5">
            <Link
              to="/searches/$searchId"
              params={{ searchId: search.id }}
              className="hover:underline"
            >
              {search.puesto}
            </Link>
            {search.technologies.length > 0 && (
              <span className="ml-2">
                · {search.technologies.slice(0, 3).join(", ")}
                {search.technologies.length > 3 && "…"}
              </span>
            )}
          </div>
        )}
      </div>
      <Link
        to="/generate"
        className="text-muted-foreground hover:text-foreground transition-colors"
        aria-label="Quitar vínculo"
      >
        <X className="h-4 w-4" />
      </Link>
    </div>
  );
}

// =================== HELPERS ===================
function defaultExpiry(): string {
  // Default: 7 días desde ahora, en formato datetime-local.
  const d = new Date();
  d.setDate(d.getDate() + 7);
  d.setSeconds(0, 0);
  // Format YYYY-MM-DDTHH:mm en local time
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
